import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { siteDiaryAPI } from '../utils/api';
import { toast } from 'sonner';
import SignaturePad from '../components/site-diary/SignaturePad';
import { Loader2, CheckCircle2, FileDown, Cloud, Users, Truck, Package, TrendingUp, ShieldCheck, AlertTriangle, Lock } from 'lucide-react';

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-3 text-sm"><Icon className="w-4 h-4 text-teal-600" />{title}</h3>
    {children}
  </div>
);

const MiniTable = ({ head, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead><tr className="text-left text-xs uppercase text-slate-500 bg-slate-50">{head.map((h) => <th key={h} className="px-2 py-1.5 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 ? <tr><td colSpan={head.length} className="px-2 py-3 text-center text-slate-400">None</td></tr>
          : rows.map((r, i) => <tr key={i} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className="px-2 py-1.5 text-slate-700">{c}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);

export default function ClientDiaryPortal() {
  const { token } = useParams();
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [thumbs, setThumbs] = useState({});
  const [name, setName] = useState('');
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await siteDiaryAPI.publicGet(token);
      setDiary(res.data);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'This link is invalid or has expired.' : 'Unable to load this diary.');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const media = (diary?.media || []).filter((m) => (m.mime_type || '').startsWith('image/'));
    media.forEach(async (m) => {
      if (thumbs[m.id]) return;
      try { const b = await siteDiaryAPI.publicMediaBlob(token, m.id); setThumbs((t) => ({ ...t, [m.id]: URL.createObjectURL(b.data) })); } catch { /* noop */ }
    });
  }, [diary, thumbs, token]);

  const submitSignature = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!sig) { toast.error('Please add your signature'); return; }
    setBusy(true);
    try {
      await siteDiaryAPI.publicSign(token, { client_rep_name: name.trim(), signature: sig });
      setSigned(true);
      toast.success('Signature recorded — thank you!');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit signature'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center"><AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" /><p className="text-slate-600">{error}</p></div>
    </div>
  );

  const d = diary;
  const alreadySigned = !!d.client_rep_name || signed;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-teal-700 text-white px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-teal-200">MBConstruction</p>
          <h1 className="text-lg font-bold">Daily Site Diary</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-slate-800">{d.project_name} {d.project_code && <span className="text-xs text-slate-400">{d.project_code}</span>}</div>
              <div className="text-sm text-slate-500">{d.diary_date}</div>
            </div>
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${d.status === 'signed_off' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
              {d.status === 'signed_off' ? 'Approved' : 'Draft / Under review'}
            </span>
          </div>
          <a href={siteDiaryAPI.publicPdfUrl(token)} target="_blank" rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 text-sm text-teal-700 hover:text-teal-900 font-medium">
            <FileDown className="w-4 h-4" /> Download PDF summary
          </a>
        </div>

        <Section icon={Cloud} title="Weather & Conditions">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-400">AM:</span> {d.weather_am_conditions || '—'} {d.weather_am_temp_c != null ? `· ${d.weather_am_temp_c}°C` : ''}</div>
            <div><span className="text-slate-400">PM:</span> {d.weather_pm_conditions || '—'} {d.weather_pm_temp_c != null ? `· ${d.weather_pm_temp_c}°C` : ''}</div>
          </div>
          {d.general_conditions && <p className="text-sm text-slate-600 mt-2">{d.general_conditions}</p>}
          {d.has_delays && <p className="text-sm text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Delay ({d.delay_type}): {d.delay_impact_hours || 0}h — {d.delay_narrative}</p>}
        </Section>

        <Section icon={Users} title="Labour">
          <MiniTable head={['Company', 'Trade', 'Head', 'Man-hrs']} rows={(d.labor || []).map((l) => [l.company_name, (l.trade_type || '').replace(/_/g, ' '), l.head_count, l.total_hours_worked])} />
        </Section>
        <Section icon={Truck} title="Plant & Equipment">
          <MiniTable head={['Equipment', 'Status', 'Hrs used']} rows={(d.equipment || []).map((e) => [e.equipment_type, (e.status || '').replace(/_/g, ' '), e.hours_used])} />
        </Section>
        <Section icon={Package} title="Material Deliveries">
          <MiniTable head={['Supplier', 'Material', 'Qty', 'Status']} rows={(d.deliveries || []).map((x) => [x.supplier_name, x.material_type, `${x.quantity} ${x.unit || ''}`, x.status])} />
        </Section>
        <Section icon={TrendingUp} title="Progress">
          <MiniTable head={['Task', '% today', '% cumul.', 'Status']} rows={(d.progress || []).map((p) => [p.task_description, p.pct_complete_today, p.pct_complete_cumulative ?? '—', (p.status || '').replace(/_/g, ' ')])} />
        </Section>
        {d.safety && (
          <Section icon={ShieldCheck} title="Safety & HSEQ">
            <div className="text-sm text-slate-600 space-y-1">
              <div>PPE compliance: <span className="font-medium">{d.safety.ppe_compliance_pct}%</span></div>
              <div>Toolbox talk: {d.safety.toolbox_talk_conducted ? (d.safety.toolbox_talk_topic || 'Yes') : 'No'}</div>
              <div>Incidents today: {d.safety.has_incidents ? 'Yes' : 'No'}</div>
            </div>
          </Section>
        )}

        {(d.media || []).filter((m) => (m.mime_type || '').startsWith('image/')).length > 0 && (
          <Section icon={Package} title="Site Photos">
            <div className="grid grid-cols-3 gap-2">
              {(d.media || []).filter((m) => (m.mime_type || '').startsWith('image/')).map((m) => (
                <div key={m.id} className="aspect-square bg-slate-100 rounded-lg overflow-hidden">
                  {thumbs[m.id] ? <img src={thumbs[m.id]} alt={m.caption || m.file_name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Sign-off */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
          <h3 className="font-semibold text-slate-800 mb-1">Site Manager sign-off</h3>
          <p className="text-sm text-slate-500 mb-3">{d.signed_off_by_name ? `Approved by ${d.signed_off_by_name} on ${(d.signed_off_at || '').slice(0, 16).replace('T', ' ')}` : 'Not yet approved'}</p>

          {alreadySigned ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-slate-700 font-medium">Client counter-signature recorded</p>
              <p className="text-xs text-slate-400">{d.client_rep_name || name}</p>
            </div>
          ) : d.status !== 'signed_off' ? (
            <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 text-slate-500 rounded-lg px-3 py-3">
              <Lock className="w-4 h-4" /> This diary is awaiting approval by the site manager before it can be counter-signed.
            </div>
          ) : (
            <div className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="portal-name"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" />
              <SignaturePad label="Your signature" onChange={setSig} />
              <button onClick={submitSignature} disabled={busy} data-testid="portal-sign-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Confirm & Sign
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-slate-400 pb-6">Read-only client view · MBConstruction Operations</p>
      </div>
    </div>
  );
}
